import React, { useEffect, useState } from 'react';
import { api } from '../services/api';

function SftpSetup() {
  const [systems, setSystems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [revealedPasswords, setRevealedPasswords] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'systems' | 'integrations'>('systems');

  // Add system form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newId, setNewId] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [addResult, setAddResult] = useState<any>(null);
  const [addError, setAddError] = useState('');

  useEffect(() => {
    api.getSystems()
      .then(data => setSystems(data.systems || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const togglePassword = (sysId: string) => {
    setRevealedPasswords(prev => {
      const next = new Set(prev);
      next.has(sysId) ? next.delete(sysId) : next.add(sysId);
      return next;
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleAddSystem = async () => {
    if (!newId.trim() || !newLabel.trim()) { setAddError('Both fields required'); return; }
    setAdding(true);
    setAddError('');
    try {
      const result = await api.createSystem(newId.trim(), newLabel.trim());
      setAddResult(result);
      // Refresh systems list
      const data = await api.getSystems();
      setSystems(data.systems || []);
    } catch (err: any) {
      setAddError(err.message || 'Failed to create system');
    }
    setAdding(false);
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div>
      <div className="page-header">
        <h1>Systems</h1>
        <p>Manage phone systems, SFTP connections, and integrations</p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16 }}>
        <button type="button" onClick={() => setActiveTab('systems')}
          style={{ padding: '10px 20px', border: '1px solid #ddd', borderBottom: activeTab === 'systems' ? '2px solid #00bcd4' : '1px solid #ddd', background: activeTab === 'systems' ? '#fff' : '#f5f5f5', cursor: 'pointer', fontWeight: activeTab === 'systems' ? 600 : 400, borderRadius: '6px 6px 0 0', fontSize: 14 }}>
          Systems ({systems.filter(s => s.active).length})
        </button>
        <button type="button" onClick={() => setActiveTab('integrations')}
          style={{ padding: '10px 20px', border: '1px solid #ddd', borderBottom: activeTab === 'integrations' ? '2px solid #00bcd4' : '1px solid #ddd', background: activeTab === 'integrations' ? '#fff' : '#f5f5f5', cursor: 'pointer', fontWeight: activeTab === 'integrations' ? 600 : 400, borderRadius: '6px 6px 0 0', fontSize: 14 }}>
          Integrations
        </button>
      </div>

      {/* ===== SYSTEMS TAB ===== */}
      {activeTab === 'systems' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button onClick={() => { setShowAddForm(!showAddForm); setAddResult(null); setAddError(''); }}
              style={{ padding: '8px 16px', background: '#00bcd4', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {showAddForm ? 'Cancel' : '+ Add System'}
            </button>
          </div>

          {/* Add System Form */}
          {showAddForm && !addResult && (
            <div className="setup-card" style={{ marginBottom: 16, borderLeft: '4px solid #00bcd4' }}>
              <h3 style={{ margin: '0 0 12px 0' }}>Add New SFTP System</h3>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>System ID</label>
                  <input type="text" value={newId} onChange={e => setNewId(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    placeholder="e.g. five9, genesys" style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, width: 180 }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>Display Name</label>
                  <input type="text" value={newLabel} onChange={e => setNewLabel(e.target.value)}
                    placeholder="e.g. Five9 (Sales)" style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, width: 220 }} />
                </div>
                <button onClick={handleAddSystem} disabled={adding}
                  style={{ padding: '8px 20px', background: adding ? '#999' : '#27ae60', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, cursor: 'pointer' }}>
                  {adding ? 'Creating...' : 'Create System'}
                </button>
              </div>
              {addError && <p style={{ color: '#c62828', fontSize: 13, marginTop: 8 }}>{addError}</p>}
              <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
                SFTP username will be <code>sftp-{newId || '<id>'}</code>. The system will be automatically provisioned on the NAS overnight.
              </p>
            </div>
          )}

          {/* Add System Result */}
          {addResult && (
            <div className="setup-card" style={{ marginBottom: 16, borderLeft: '4px solid #27ae60' }}>
              <h3 style={{ margin: '0 0 8px 0', color: '#2e7d32' }}>System Created: {addResult.system?.label}</h3>
              <div style={{ padding: '12px 16px', background: '#fff8e1', borderRadius: 8, marginBottom: 16, border: '1px solid #ffe082' }}>
                <p style={{ margin: 0, fontSize: 14, color: '#f57f17', fontWeight: 600 }}>
                  Provisioning in progress
                </p>
                <p style={{ margin: '4px 0 0 0', fontSize: 13, color: '#795548' }}>
                  The SFTP user, directories, and server config will be automatically set up on the NAS overnight.
                  Connection details will appear on the Systems page once provisioning is complete.
                  You can add the vendor's source IP on the <strong>Access Control</strong> page at any time.
                </p>
              </div>
              <p style={{ fontSize: 14, marginBottom: 12 }}>Connection details to share with the vendor:</p>
              <table style={{ fontSize: 14, borderCollapse: 'collapse', marginBottom: 16 }}>
                <tbody>
                  {[
                    ['Host', addResult.system?.connection?.host],
                    ['Port', addResult.system?.connection?.port],
                    ['Username', addResult.system?.connection?.username],
                    ['Upload Folder', addResult.system?.connection?.uploadFolder],
                    ['Password', 'Will be available on Systems page after provisioning'],
                  ].map(([label, value]) => (
                    <tr key={label as string} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '6px 16px 6px 0', fontWeight: 600 }}>{label}</td>
                      <td style={{ padding: '6px 0' }}><code>{String(value)}</code></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button onClick={() => { setShowAddForm(false); setAddResult(null); setNewId(''); setNewLabel(''); }}
                style={{ marginTop: 12, padding: '6px 16px', background: '#fff', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                Done
              </button>
            </div>
          )}

          {/* Existing Systems */}
          {systems.map(system => (
            <div className="setup-card" key={system.id} style={{ marginBottom: 16 }}>
              <h3>
                {system.label}
                <span className={`status badge ${system.active ? 'badge-success' : 'badge-warning'}`} style={{ marginLeft: 8 }}>
                  {system.active ? 'Active' : 'Inactive'}
                </span>
                {system.custom && <span className="badge badge-info" style={{ marginLeft: 8 }}>Custom</span>}
                {system.provisioningStatus === 'pending' && <span className="badge badge-warning" style={{ marginLeft: 8 }}>Provisioning</span>}
              </h3>

              {system.connection ? (
                <>
                  <h4>Connection Details</h4>
                  <table style={{ marginBottom: 20, fontSize: 15, borderCollapse: 'collapse', width: '100%', maxWidth: 600 }}>
                    <tbody>
                      {[
                        ['Host', system.connection.host],
                        ['Port', system.connection.port],
                        ['Username', system.connection.username],
                        ['Upload Folder', system.connection.uploadFolder],
                      ].map(([label, value], i) => (
                        <tr key={label} style={{ background: i % 2 === 0 ? '#f8f9fa' : '#fff' }}>
                          <td style={{ padding: '10px 20px 10px 12px', fontWeight: 700, color: '#333', width: 140 }}>{label}</td>
                          <td style={{ padding: '10px 12px' }}><code style={{ fontSize: 15 }}>{value}</code></td>
                        </tr>
                      ))}
                      <tr style={{ background: '#f8f9fa' }}>
                        <td style={{ padding: '10px 20px 10px 12px', fontWeight: 700, color: '#333', width: 140 }}>Password</td>
                        <td style={{ padding: '10px 12px' }}>
                          {system.connection.password === 'Restricted — admin access required' ? (
                            <span style={{ color: '#999', fontSize: 13 }}>{system.connection.password}</span>
                          ) : system.connection.password === 'Contact NAS admin for password' ? (
                            <span style={{ color: '#999', fontSize: 13 }}>Set during NAS setup</span>
                          ) : system.connection.password === 'Not stored — set on NAS but not recorded in app' ? (
                            <span style={{ color: '#e65100', fontSize: 13 }}>{system.connection.password}</span>
                          ) : (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <code style={{ fontSize: 15 }}>
                                {revealedPasswords.has(system.id) ? system.connection.password : '••••••••••••••••'}
                              </code>
                              <button type="button" onClick={() => togglePassword(system.id)}
                                style={{ padding: '2px 8px', fontSize: 12, cursor: 'pointer', border: '1px solid #ddd', borderRadius: 4, background: '#fff' }}>
                                {revealedPasswords.has(system.id) ? 'Hide' : 'Show'}
                              </button>
                              {revealedPasswords.has(system.id) && (
                                <button type="button" onClick={() => copyToClipboard(system.connection.password)}
                                  style={{ padding: '2px 8px', fontSize: 12, cursor: 'pointer', border: '1px solid #ddd', borderRadius: 4, background: '#fff' }}>
                                  Copy
                                </button>
                              )}
                            </span>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  {system.namingConvention && (
                    <>
                      <h4>Filename Naming Convention</h4>
                      <div style={{ padding: '12px 16px', background: '#f0f4ff', borderRadius: 8, marginBottom: 16, fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all' }}>
                        {system.namingConvention.pattern}
                      </div>
                      <table style={{ marginBottom: 16, fontSize: 13, borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                          <tr style={{ background: '#f8f9fa' }}>
                            <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Field</th>
                            <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Description</th>
                            <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Required</th>
                            <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Example</th>
                          </tr>
                        </thead>
                        <tbody>
                          {system.namingConvention.fields.map((f: any) => (
                            <tr key={f.name} style={{ borderBottom: '1px solid #eee' }}>
                              <td style={{ padding: '8px 12px', fontWeight: 600 }}>{f.name}</td>
                              <td style={{ padding: '8px 12px' }}>{f.description}</td>
                              <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                {f.required === 'recommended' ? <span className="badge badge-warning">Recommended</span>
                                  : f.required ? <span className="badge badge-danger">Required</span>
                                  : <span className="badge badge-info">Optional</span>}
                              </td>
                              <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{f.example}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <h4>Examples</h4>
                      {system.namingConvention.examples.map((ex: string, i: number) => (
                        <div key={i} style={{ padding: '8px 12px', background: '#f8f9fa', borderRadius: 6, marginBottom: 6, fontFamily: 'monospace', fontSize: 12 }}>{ex}</div>
                      ))}
                      {system.namingConvention.notes?.length > 0 && (
                        <>
                          <h4 style={{ marginTop: 16 }}>Important Notes</h4>
                          <ul style={{ paddingLeft: 20, fontSize: 14, lineHeight: 1.8 }}>
                            {system.namingConvention.notes.map((note: string, i: number) => <li key={i}>{note}</li>)}
                          </ul>
                        </>
                      )}
                    </>
                  )}

                  {system.notes?.length > 0 && (
                    <>
                      <h4>Setup Notes</h4>
                      <ul style={{ paddingLeft: 20, fontSize: 14, lineHeight: 1.8 }}>
                        {system.notes.map((note: string, i: number) => <li key={i}>{note}</li>)}
                      </ul>
                    </>
                  )}
                </>
              ) : (
                <p style={{ color: '#888', fontSize: 14 }}>{system.notes?.[0] || 'No connection configured.'}</p>
              )}
            </div>
          ))}
        </>
      )}

      {/* ===== INTEGRATIONS TAB ===== */}
      {activeTab === 'integrations' && (
        <>
          <div className="setup-card">
            <h3>How Each System Works</h3>
            <div style={{ fontSize: 14, lineHeight: 1.8, color: '#555' }}>
              <h4 style={{ color: '#333', marginBottom: 4 }}>PulseDial (API-based)</h4>
              <p>Call ends in Twilio. PulseDial downloads the recording, transcribes it with AI, and sends everything to Pulse Storage through our API. The audio goes straight to S3 (Amazon's cloud storage) first, then syncs to the NAS for fast local playback. Once both S3 and NAS confirm they have the file, PulseDial deletes it from Twilio to save costs. If the NAS is down, recordings are still safe in S3 — nothing is lost.</p>
              <h4 style={{ color: '#333', marginBottom: 4, marginTop: 16 }}>DialedIn / Vicidial (SFTP-based)</h4>
              <p>These systems upload recording files directly to the NAS via SFTP. Every 5 minutes, a script picks up new files, validates them, renames them to a standard format, and archives them. A backup copy is immediately uploaded to S3. If a file fails validation (wrong format, too small, duplicate), it goes to quarantine and shows up on the Quarantine page so you can ask the vendor to resend.</p>
              <h4 style={{ color: '#333', marginBottom: 4, marginTop: 16 }}>Storage Lifecycle</h4>
              <table className="data-table" style={{ fontSize: 13, marginBottom: 12 }}>
                <thead><tr><th>Time</th><th>S3 Storage</th><th>On NAS?</th><th>Playback</th></tr></thead>
                <tbody>
                  <tr><td>First 30 days</td><td>S3 Standard</td><td>Yes</td><td>Instant</td></tr>
                  <tr><td>30 - 120 days</td><td>S3 Standard-IA (cheaper)</td><td>Yes</td><td>Instant</td></tr>
                  <tr><td>120 days - 3 years</td><td>Glacier Deep Archive</td><td>Yes</td><td>Instant</td></tr>
                  <tr><td>3 - 10 years</td><td>Glacier Deep Archive</td><td>No (freed)</td><td>Request from Archive (12-48hr)</td></tr>
                  <tr><td>After 10 years</td><td>Deleted</td><td>No</td><td>Medicare retention complete</td></tr>
                </tbody>
              </table>
              <p>Every recording exists in two places (NAS + S3) for the first 3 years. After 3 years, the NAS copy is removed to free space, but the S3 Glacier copy remains for the full 10-year Medicare retention.</p>
              <h4 style={{ color: '#333', marginBottom: 4, marginTop: 16 }}>Estimated S3 Cost (500 calls/day, 5 min avg)</h4>
              <table className="data-table" style={{ fontSize: 13, marginBottom: 12 }}>
                <thead><tr><th></th><th>Data</th><th>Monthly Cost</th></tr></thead>
                <tbody>
                  <tr><td>New recordings/month</td><td>~75 GB</td><td></td></tr>
                  <tr><td>S3 Standard (first 30 days)</td><td>75 GB</td><td>~$1.73</td></tr>
                  <tr><td>S3 Standard-IA (30-120 days)</td><td>~225 GB</td><td>~$2.81</td></tr>
                  <tr><td>Glacier Deep Archive (after 120 days)</td><td>Grows over time</td><td>~$0.001/GB</td></tr>
                  <tr><td><strong>Year 1 total</strong></td><td>~900 GB</td><td><strong>~$6/month</strong></td></tr>
                  <tr><td><strong>Year 3 total</strong></td><td>~2.7 TB</td><td><strong>~$8/month</strong></td></tr>
                  <tr><td><strong>Year 10 total</strong></td><td>~9 TB</td><td><strong>~$12/month</strong></td></tr>
                </tbody>
              </table>
              <p>Based on 500 calls/day at 5 min avg, MP3 128kbps (~5 MB per call). Glacier Deep Archive is $0.00099/GB/month — extremely cheap for long-term storage. Total 10-year cost is roughly <strong>$100-150/year</strong> for full Medicare-compliant backup of all recordings.</p>
            </div>
          </div>

          <div className="setup-card" style={{ marginTop: 16 }}>
            <h3>Recording Ingest Pipeline (Technical)</h3>
            <div style={{ fontSize: 14, lineHeight: 1.8, color: '#555' }}>
              <p><strong>How PulseDial sends recordings:</strong></p>
              <ol style={{ paddingLeft: 20 }}>
                <li>Call ends. PulseDial downloads audio from Twilio and transcribes it (Whisper + GPT).</li>
                <li>PulseDial POSTs metadata + transcript to <code>POST /api/v1/recordings/ingest</code> with the API key. Returns a pre-signed S3 upload URL.</li>
                <li>PulseDial uploads the MP3 directly to S3 using the pre-signed URL.</li>
                <li>PulseDial confirms the upload: <code>POST /api/v1/recordings/confirm/{'recordingId'}</code>. Lambda verifies the file is in S3 and marks it <strong>s3_confirmed</strong>.</li>
                <li>NAS syncs from S3 every 5 minutes. After ingestion, status updates to <strong>fully_archived</strong>.</li>
                <li>PulseDial polls <code>GET /api/v1/recordings/status/{'recordingId'}</code>. When <code>canDeleteFromSource: true</code>, PulseDial deletes the recording from Twilio.</li>
              </ol>
              <p><strong>Why this flow?</strong></p>
              <ul style={{ paddingLeft: 20 }}>
                <li>Recordings go to S3 first (99.999999999% durability) — if the NAS is down, recordings are still safe.</li>
                <li>Twilio recordings are only deleted after confirmed in BOTH S3 and NAS.</li>
              </ul>
              <p><strong>For DialedIn / Vicidial</strong> (SFTP): Files upload to NAS directly. Ingestion cron processes them every 5 minutes and uploads a copy to S3 as backup.</p>
            </div>
          </div>

          <div className="setup-card" style={{ marginTop: 16 }}>
            <h3>API Endpoints Reference</h3>
            <table className="data-table" style={{ fontSize: 13 }}>
              <thead><tr><th>Method</th><th>Endpoint</th><th>Description</th></tr></thead>
              <tbody>
                <tr><td><span className="badge badge-success">POST</span></td><td><code>/api/v1/recordings/ingest</code></td><td>Submit metadata + transcript, get S3 upload URL</td></tr>
                <tr><td><span className="badge badge-success">POST</span></td><td><code>/api/v1/recordings/confirm/{'recordingId'}</code></td><td>Confirm audio uploaded to S3</td></tr>
                <tr><td><span className="badge badge-info">GET</span></td><td><code>/api/v1/recordings/status/{'recordingId'}</code></td><td>Check storage status (s3 + NAS)</td></tr>
                <tr><td><span className="badge badge-info">GET</span></td><td><code>/api/v1/recordings/{'callId'}</code></td><td>Look up recording by call ID</td></tr>
                <tr><td><span className="badge badge-info">GET</span></td><td><code>/api/v1/recordings/{'callId'}/transcript</code></td><td>Get transcript for a recording</td></tr>
                <tr><td><span className="badge badge-info">GET</span></td><td><code>/api/v1/recordings?systemId=&date=</code></td><td>List recordings</td></tr>
              </tbody>
            </table>
            <p style={{ marginTop: 12, fontSize: 13, color: '#888' }}>
              All endpoints require <code>X-Api-Key</code> header. API keys are managed on the <strong>API Keys</strong> page.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

export default SftpSetup;
